import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const AdminAttendanceManagement = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('mark');
  const [gpsSettings, setGpsSettings] = useState({
    enabled: true,
    studentGpsEnabled: true,
    studentAutoCheckIn: true,
    teacherAutoCheckIn: true,
    staffAutoCheckIn: true,
    latitude: '',
    longitude: '',
    rangeMeters: 15.24,
    accuracyMeters: 20
  });

  // Reports state
  const [reportsFilter, setReportsFilter] = useState({
    className: 'all',
    month: new Date().getMonth(),
    year: new Date().getFullYear()
  });
  const [attendanceReports, setAttendanceReports] = useState([]);
  const [reportStats, setReportStats] = useState({});

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass && selectedDate) {
      loadAttendanceData();
    }
  }, [selectedClass, selectedDate]);

  const loadInitialData = async () => {
    try {
      const [classesRes, studentsRes] = await Promise.all([
        fetch(API_ENDPOINTS.CLASSES),
        fetch(API_ENDPOINTS.STUDENTS)
      ]);

      const classesData = await classesRes.json();
      const studentsData = await studentsRes.json();

      setClasses(classesData);
      setStudents(studentsData);
      const gpsRes = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS);
      if (gpsRes.ok) setGpsSettings(await gpsRes.json());

      if (classesData.length > 0) {
        setSelectedClass(classesData[0]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const saveGpsSettings = async () => {
    const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gpsSettings)
    });
    const data = await response.json();
    if (!response.ok) return alert(data.error || 'Failed to save GPS settings');
    setGpsSettings(data.settings);
    alert('GPS attendance settings saved.');
  };

  const loadAttendanceData = async () => {
    if (!selectedClass || !selectedDate) return;

    setLoading(true);
    try {
      // Load existing attendance for the selected date and class
      const classStudents = students.filter(student => student.className === selectedClass);
      const attendanceMap = {};

      // Check attendance for each student
      for (const student of classStudents) {
        try {
          const response = await fetch(`${API_ENDPOINTS.ATTENDANCE}/${student.username}`);
          const studentAttendance = await response.json();

          // Find attendance for selected date
          const dateAttendance = studentAttendance.find(att => att.date === selectedDate);
          attendanceMap[student.username] = dateAttendance ? dateAttendance.status : 'not-marked';
        } catch (error) {
          console.error(`Error loading attendance for ${student.username}:`, error);
          attendanceMap[student.username] = 'not-marked';
        }
      }

      setAttendanceData(attendanceMap);
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAttendance = async (username, status) => {
    try {
      const attendanceRecord = {
        username,
        date: selectedDate,
        status,
        markedBy: 'admin',
        className: selectedClass
      };

      const response = await fetch(API_ENDPOINTS.ATTENDANCE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attendanceRecord)
      });

      if (response.ok) {
        setAttendanceData(prev => ({
          ...prev,
          [username]: status
        }));
      } else {
        alert('Failed to update attendance');
      }
    } catch (error) {
      console.error('Error updating attendance:', error);
      alert('Failed to update attendance');
    }
  };

  const markAllPresent = () => {
    const classStudents = students.filter(student => student.className === selectedClass);
    classStudents.forEach(student => {
      if (attendanceData[student.username] !== 'present') {
        updateAttendance(student.username, 'present');
      }
    });
  };

  const generateReports = async () => {
    try {
      const classStudents = reportsFilter.className === 'all'
        ? students
        : students.filter(student => student.className === reportsFilter.className);

      const reports = [];
      const stats = {
        totalStudents: classStudents.length,
        presentDays: 0,
        absentDays: 0,
        lateDays: 0,
        totalDays: 0
      };

      for (const student of classStudents) {
        try {
          const response = await fetch(`${API_ENDPOINTS.ATTENDANCE}/${student.username}`);
          const attendanceRecords = await response.json();

          // Filter by month/year
          const monthlyAttendance = attendanceRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate.getMonth() === reportsFilter.month &&
                   recordDate.getFullYear() === reportsFilter.year;
          });

          const presentCount = monthlyAttendance.filter(r => r.status === 'present').length;
          const absentCount = monthlyAttendance.filter(r => r.status === 'absent').length;
          const lateCount = monthlyAttendance.filter(r => r.status === 'late').length;
          const totalDays = monthlyAttendance.length;

          stats.presentDays += presentCount;
          stats.absentDays += absentCount;
          stats.lateDays += lateCount;
          stats.totalDays += totalDays;

          reports.push({
            student: `${student.firstName} ${student.lastName}`,
            className: student.className,
            present: presentCount,
            absent: absentCount,
            late: lateCount,
            totalDays,
            attendanceRate: totalDays > 0 ? Math.round((presentCount / totalDays) * 100) : 0
          });
        } catch (error) {
          console.error(`Error generating report for ${student.username}:`, error);
        }
      }

      setAttendanceReports(reports);
      setReportStats(stats);
    } catch (error) {
      console.error('Error generating reports:', error);
    }
  };

  const exportAttendanceReport = () => {
    if (attendanceReports.length === 0) {
      alert('No report data to export');
      return;
    }

    const csvData = [
      ['Student Name', 'Class', 'Present Days', 'Absent Days', 'Late Days', 'Total Days', 'Attendance Rate (%)'],
      ...attendanceReports.map(report => [
        report.student,
        report.className,
        report.present,
        report.absent,
        report.late,
        report.totalDays,
        report.attendanceRate
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${reportsFilter.year}-${reportsFilter.month + 1}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const classStudents = students.filter(student => student.className === selectedClass);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Attendance Management</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('mark')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'mark'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Mark Attendance
              </button>
              <button
                onClick={() => setActiveTab('reports')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'reports'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Reports & Analytics
              </button>
              <button
                onClick={() => setActiveTab('gps')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'gps'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                GPS Auto Check-in
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'mark' && (
              <div>
                <div className="flex flex-wrap gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Class</label>
                    <select
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                      className="border rounded px-3 py-2"
                    >
                      <option value="">Select Class</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="border rounded px-3 py-2"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={markAllPresent}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                      Mark All Present
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="text-xl text-blue-600">Loading attendance data...</div>
                  </div>
                ) : selectedClass ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Student Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classStudents.map((student) => (
                          <tr key={student.username} className="border-t">
                            <td className="px-4 py-3 text-sm text-gray-800">
                              {student.firstName} {student.lastName}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${
                                attendanceData[student.username] === 'present'
                                  ? 'bg-green-100 text-green-800'
                                  : attendanceData[student.username] === 'absent'
                                  ? 'bg-red-100 text-red-800'
                                  : attendanceData[student.username] === 'late'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {attendanceData[student.username] === 'not-marked'
                                  ? 'Not Marked'
                                  : attendanceData[student.username]?.charAt(0).toUpperCase() + attendanceData[student.username]?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm space-x-2">
                              <button
                                onClick={() => updateAttendance(student.username, 'present')}
                                className={`px-3 py-1 rounded text-xs ${
                                  attendanceData[student.username] === 'present'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                Present
                              </button>
                              <button
                                onClick={() => updateAttendance(student.username, 'absent')}
                                className={`px-3 py-1 rounded text-xs ${
                                  attendanceData[student.username] === 'absent'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                                }`}
                              >
                                Absent
                              </button>
                              <button
                                onClick={() => updateAttendance(student.username, 'late')}
                                className={`px-3 py-1 rounded text-xs ${
                                  attendanceData[student.username] === 'late'
                                    ? 'bg-yellow-600 text-white'
                                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                }`}
                              >
                                Late
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-xl text-gray-500">Please select a class to manage attendance</div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'gps' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">GPS Attendance Rules</h2>
                  <p className="text-gray-600 mt-1">Set the school location, allowed check-in radius, and GPS accuracy rules used by students, teachers, and staff.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ['latitude', 'School Latitude'],
                    ['longitude', 'School Longitude'],
                    ['rangeMeters', 'Allowed Range (meters)'],
                    ['accuracyMeters', 'Required GPS Accuracy (meters)']
                  ].map(([key, label]) => (
                    <label key={key} className="block">
                      <span className="block text-sm font-semibold text-gray-700 mb-1">{label}</span>
                      <input
                        type="number"
                        step="any"
                        value={gpsSettings[key]}
                        onChange={(event) => setGpsSettings(current => ({ ...current, [key]: event.target.value }))}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    ['enabled', 'Enable GPS validation'],
                    ['studentGpsEnabled', 'Student GPS attendance'],
                    ['studentAutoCheckIn', 'Student GPS auto check-in'],
                    ['teacherAutoCheckIn', 'Teacher auto check-in'],
                    ['staffAutoCheckIn', 'Staff auto check-in']
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <input
                        type="checkbox"
                        checked={Boolean(gpsSettings[key])}
                        onChange={(event) => setGpsSettings(current => ({ ...current, [key]: event.target.checked }))}
                      />
                      <span className="font-semibold text-blue-900">{label}</span>
                    </label>
                  ))}
                </div>
                <button onClick={saveGpsSettings} className="bg-blue-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-800">
                  Save GPS Settings
                </button>
              </div>
            )}

            {activeTab === 'reports' && (
              <div>
                <div className="flex flex-wrap gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                    <select
                      value={reportsFilter.className}
                      onChange={(e) => setReportsFilter({...reportsFilter, className: e.target.value})}
                      className="border rounded px-3 py-2"
                    >
                      <option value="all">All Classes</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <select
                      value={reportsFilter.month}
                      onChange={(e) => setReportsFilter({...reportsFilter, month: parseInt(e.target.value)})}
                      className="border rounded px-3 py-2"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i} value={i}>
                          {new Date(0, i).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                      value={reportsFilter.year}
                      onChange={(e) => setReportsFilter({...reportsFilter, year: parseInt(e.target.value)})}
                      className="border rounded px-3 py-2"
                    >
                      {[2024, 2025, 2026].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end space-x-2">
                    <button
                      onClick={generateReports}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Generate Report
                    </button>
                    {attendanceReports.length > 0 && (
                      <button
                        onClick={exportAttendanceReport}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      >
                        Export CSV
                      </button>
                    )}
                  </div>
                </div>

                {/* Report Statistics */}
                {Object.keys(reportStats).length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{reportStats.totalStudents}</div>
                      <div className="text-sm text-blue-800">Total Students</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {reportStats.totalDays > 0 ? Math.round((reportStats.presentDays / reportStats.totalDays) * 100) : 0}%
                      </div>
                      <div className="text-sm text-green-800">Avg Attendance</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-600">{reportStats.absentDays}</div>
                      <div className="text-sm text-red-800">Total Absences</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">{reportStats.lateDays}</div>
                      <div className="text-sm text-yellow-800">Late Arrivals</div>
                    </div>
                  </div>
                )}

                {/* Attendance Report Table */}
                {attendanceReports.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Student Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Class</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Present</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Absent</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Late</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Attendance Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceReports.map((report, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-4 py-3 text-sm text-gray-800">{report.student}</td>
                            <td className="px-4 py-3 text-sm text-gray-800">{report.className}</td>
                            <td className="px-4 py-3 text-sm text-green-600 font-semibold">{report.present}</td>
                            <td className="px-4 py-3 text-sm text-red-600 font-semibold">{report.absent}</td>
                            <td className="px-4 py-3 text-sm text-yellow-600 font-semibold">{report.late}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${
                                report.attendanceRate >= 90 ? 'bg-green-100 text-green-800' :
                                report.attendanceRate >= 75 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {report.attendanceRate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAttendanceManagement;



